import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';

interface Props {
  readonly top: ReactNode;
  readonly bottom: ReactNode;
  readonly label: string;
}

const KEY = 'simulador-cohetes:split';
export const SPLIT_MIN = 0.2;
export const SPLIT_MAX = 0.8;
export const SPLIT_DEFAULT = 0.5;
const STEP = 0.05;

const clamp = (v: number): number => Math.min(Math.max(v, SPLIT_MIN), SPLIT_MAX);

function readRatio(): number {
  try {
    const raw = localStorage.getItem(KEY);
    const v = raw === null ? NaN : Number(raw);
    return Number.isFinite(v) ? clamp(v) : SPLIT_DEFAULT;
  } catch {
    return SPLIT_DEFAULT;
  }
}

function writeRatio(v: number): void {
  try {
    localStorage.setItem(KEY, String(v));
  } catch {
    // sin almacenamiento: la proporción solo dura la sesión
  }
}

/** Dos mitades, una sobre otra, con un separador que se arrastra o se mueve con el teclado. */
export function SplitPane({ top, bottom, label }: Props): JSX.Element {
  const [ratio, setRatio] = useState(readRatio);
  const box = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const latest = useRef(ratio);
  if (!dragging.current) latest.current = ratio;
  const frameId = useRef<number | undefined>(undefined);
  useEffect(() => () => {
    if (frameId.current !== undefined) cancelAnimationFrame(frameId.current);
  }, []);

  const commit = (v: number): void => {
    const next = clamp(v);
    setRatio(next);
    writeRatio(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    const next = e.key === 'ArrowUp' ? ratio - STEP : e.key === 'ArrowDown' ? ratio + STEP : e.key === 'Home' ? SPLIT_MIN : e.key === 'End' ? SPLIT_MAX : undefined;
    if (next === undefined) return;
    e.preventDefault();
    commit(next);
  };
  const move = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const r = box.current?.getBoundingClientRect();
    if (!dragging.current || r === undefined || r.height === 0) return;
    const next = clamp((e.clientY - r.top) / r.height);
    latest.current = next;
    // Al arrastrar llegan más movimientos que fotogramas: se aplica solo el último de cada fotograma.
    if (typeof requestAnimationFrame === 'undefined') setRatio(next);
    else if (frameId.current === undefined) {
      frameId.current = requestAnimationFrame(() => {
        frameId.current = undefined;
        setRatio(latest.current);
      });
    }
  };

  return (
    <div className="split" ref={box} style={{ gridTemplateRows: `minmax(0, ${ratio}fr) auto minmax(0, ${1 - ratio}fr)` }}>
      <div className="split__top">{top}</div>
      <div
        className="split__bar"
        role="separator"
        aria-orientation="horizontal"
        aria-label={label}
        aria-valuemin={Math.round(SPLIT_MIN * 100)}
        aria-valuemax={Math.round(SPLIT_MAX * 100)}
        aria-valuenow={Math.round(ratio * 100)}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onPointerDown={(e) => {
          dragging.current = true;
          e.currentTarget.setPointerCapture?.(e.pointerId);
        }}
        onPointerMove={move}
        onPointerUp={(e) => {
          dragging.current = false;
          e.currentTarget.releasePointerCapture?.(e.pointerId);
          setRatio(latest.current);
          writeRatio(latest.current);
        }}
        onPointerCancel={() => {
          dragging.current = false;
        }}
      />
      <div className="split__bottom">{bottom}</div>
    </div>
  );
}
